import React, { useState, useEffect } from 'react';
import { 
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  styled
} from '@mui/material';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { postData } from '../../Webservice/Webservice';
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { getDefaultRoute } from '../../Utils/permissionUtils';

// Custom styled components
const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: 'url("./img/razimage.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // background: 'rgba(13, 71, 161, 0.75)', // Dark blue overlay for contrast
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  maxWidth: 350,
  padding: theme.spacing(5),
  paddingLeft: theme.spacing(100),
}));

const StyledForm = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(5),
  backgroundColor: 'rgba(255, 255, 255, 1)', // Semi-transparent white
  boxShadow: theme.shadows[8],
  borderRadius: "12px",
  
}));

// Professional theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0d47a1', // Deep blue
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f0f4f8',
      paper: 'rgba(255, 255, 255, 0.95)',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Inter", "Mulish", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    body1: {
      fontWeight: 500,
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          borderRadius: '12px',
          backgroundColor: 'rgb(255, 209, 2)', // Yellow button
          color: 'black',
          letterSpacing: '1px',
          fontSize: '1.1rem',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgb(255, 209, 2)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0d47a1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0d47a1',
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#424242',
          '&.Mui-focused': {
            color: '#0d47a1',
          },
        },
      },
    },
  },
});

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  // Handler for submitting OTP (implement your logic here)
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpSubmitting(true);
    try {
      const data = { email: forgotEmail, otp };
      const response = await postData('auth/otp-verify', data, null, false);
      if (response && response.message === 'OTP verified successfully') {
        setOtpVerified(true);
      }
      console.log('OTP verification response:', response);
    } catch (error) {
      console.error('OTP submit error:', error);
    } finally {
      setOtpSubmitting(false);
    }

  // Handler for password reset
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      alert('Please enter and confirm your new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
    setResetSubmitting(true);
    try {
      const data = { email: forgotEmail, password: newPassword };
      const response = await postData('auth/reset-password', data, null, false);
      if (response && response.message && response.message.toLowerCase().includes('success')) {
        alert('Password reset successful! Please login with your new password.');
        setShowForgot(false);
        setOtpSent(false);
        setOtpVerified(false);
        setForgotEmail('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        navigate('/');
      } else {
        alert(response && response.message ? response.message : 'Password reset failed.');
      }
      console.log('Password reset response:', response);
    } catch (error) {
      alert('Password reset error.');
      console.error('Password reset error:', error);
    } finally {
      setResetSubmitting(false);
    }
  };
  const navigate = useNavigate();

  // Handler for sending OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = { email: forgotEmail };
      const response = await postData('auth/forgot-password', data, null, false);
      // You can check response for success or error message
      console.log('OTP sent successfully:', response);
      setOtpSent(true);
    } catch (error) {
      console.error('Send OTP error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check localStorage first for persistence, then sessionStorage as fallback
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (token || isLoggedIn) {
      const userType = localStorage.getItem("userType") || sessionStorage.getItem("userType");
      const userDataStr = localStorage.getItem("user") || sessionStorage.getItem("user");
      let menus = [];
      if (userDataStr) {
        try {
          const user = JSON.parse(userDataStr);
          menus = user.menus || [];
        } catch (e) {
          console.error("Error parsing user data for redirect", e);
        }
      }
      const targetPath = getDefaultRoute(menus, userType);
      navigate(targetPath);
    }
  }, [navigate, isLoggedIn]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const loginData = {
        email: email,
        password: password
      };
      
      const response = await postData('auth/login', loginData, null, false);
      
      if (response.token) {
        // Clear any existing session/local data first
        sessionStorage.clear();
        localStorage.clear();
        
        // Store user data in localStorage for persistence across refreshes/new tabs
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response));
        localStorage.setItem('userType', 'common');
        localStorage.setItem('commonToken', response.token);
        localStorage.setItem('commonUser', JSON.stringify(response));
        
        const userId = response.id || response._id || (response.user && response.user._id);
        if (userId) {
          localStorage.setItem('userId', userId);
          localStorage.setItem('commonUserId', userId);
        }

        // Also sync with sessionStorage for existing components that might rely on it
        sessionStorage.setItem('token', response.token);
        sessionStorage.setItem('user', JSON.stringify(response));
        sessionStorage.setItem('userType', 'common');
        
        setIsLoggedIn(true); // Trigger redirect
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <StyledRoot>
        <StyledFormSection>
          <StyledForm elevation={0}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" component="h2" sx={{ fontFamily: 'Mulish, sans-serif' ,mb: 3 }}>
                {showForgot ? 'Forgot Password' : 'Sign In'}
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{fontFamily: 'Mulish, sans-serif'}}>
                True Value For Money
              </Typography>
            </Box>
            
            <Box component="form"
              onSubmit={
                showForgot
                  ? (otpSent
                      ? (otpVerified
                          ? handleResetPassword
                          : handleOtpSubmit)
                      : handleSendOtp)
                  : handleLoginSubmit
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(3),
              }}>
              <FormControl fullWidth>
                <TextField
                  variant="outlined"
                  placeholder="User Email"
                  value={showForgot ? forgotEmail : email}
                  onChange={showForgot ? (e) => setForgotEmail(e.target.value) : (e) => setEmail(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaUser style={{ color: '#b0b8c9', }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={otpSent && showForgot}
                />
              </FormControl>

              {!showForgot && (
                <FormControl fullWidth>
                  <TextField
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaLock style={{ color: '#b0b8c9',  }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? 
                              <FaEye style={{ color: '#424242', fontSize: '1.1rem' }} /> : 
                              <FaEyeSlash style={{ color: '#424242', fontSize: '1.1rem' }} />
                            }
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              
                {!showForgot && (
                  <Link 
                    href="#" 
                    variant="body2" 
                    onClick={(e) => { e.preventDefault(); setShowForgot(true); }}
                    sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Forgot Password?
                  </Link>
                )}
                {showForgot && (
                  <Link 
                    href="#" 
                    variant="body2" 
                    onClick={(e) => { e.preventDefault(); setShowForgot(false); setOtpSent(false); setForgotEmail(''); }}
                    sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Back to Login
                  </Link>
                )}
              </Box>

              {!showForgot && (
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              )}
              {showForgot && !otpSent && (
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={otpSent || !forgotEmail}
                >
                  {otpSent ? 'OTP Sent' : 'Send OTP'}
                </Button>
              )}
              {showForgot && otpSent && !otpVerified && (
                <>
                  <FormControl fullWidth>
                    <TextField
                      variant="outlined"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      inputProps={{ maxLength: 6, pattern: '\\d{6}' }}
                      sx={{ letterSpacing: '0.5em', textAlign: 'center' }}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={otp.length !== 6 || otpSubmitting}
                    sx={{ mt: 2 }}
                  >
                    {otpSubmitting ? 'Verifying...' : 'Submit OTP'}
                  </Button>
                </>
              )}
              {showForgot && otpSent && otpVerified && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      variant="outlined"
                      value={forgotEmail}
                      disabled
                      label="Email"
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      variant="outlined"
                      type="password"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      label="New Password"
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      variant="outlined"
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      label="Confirm Password"
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!newPassword || !confirmPassword || resetSubmitting}
                  >
                    {resetSubmitting ? 'Confirming...' : 'Confirm'}
                  </Button>
                </>
              )}
            </Box>
          </StyledForm>
        </StyledFormSection>
      </StyledRoot>
    </ThemeProvider>
  );
};

export default LoginPage;